import React, { useState, useEffect } from 'react';
import Sidebar from '../Dashboard/Sidebar';
import Header from '../Dashboard/Header';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { useLocation } from 'react-router-dom';
import './DashboardLayout.css';

const DashboardLayout = ({ children, isAdmin: isAdminProp = false }) => {
  const { user } = useAuth();
  const { admin, isAdminAuthenticated } = useAdmin();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-detect admin mode if we have an admin session
  const isAdminMode = isAdminProp || (isAdminAuthenticated && admin && !user);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(false);
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check on initial load

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Apply theme to document element
  useEffect(() => {
    const theme = user?.preferences?.theme || 'light';
    document.documentElement.setAttribute('data-color-scheme', theme);
  }, [user?.preferences?.theme]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.sidebar') && !event.target.closest('.mobile-menu-btn')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  const handleToggleSidebar = () => {
    if (window.innerWidth < 768) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <div className={`sidebar-container ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Sidebar 
          isAdmin={isAdminMode} 
          collapsed={sidebarCollapsed} 
          onToggle={handleToggleSidebar} 
        />
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}

      {/* Main Content Area */}
      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Header */}
        <Header
          isAdmin={isAdminMode}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
        />

        {/* Page Content */}
        <main className="page-content">
          <div className="content-wrapper">
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="dashboard-footer">
          <div className="footer-content">
            <div className="footer-left">
              <p>&copy; 2026 MessagePe. All rights reserved.</p>
            </div>
            <div className="footer-right">
              <a href="mailto:hey@vrittas.com">Help</a>
              <span className="separator">•</span>
              <a href="https://messagepe.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
              <span className="separator">•</span>
              <a href="https://messagepe.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DashboardLayout;